/**
 * First-run calibration: the arithmetic, with no clock and no React in it.
 *
 * ## What is worth calibrating, and what is not
 *
 * Eighteen parameters are tunable and seventeen of them describe *a piano* — how long a
 * struck note keeps contributing, how sceptical to be when the sounding set changes, how
 * much a rival must beat the incumbent by. Those are the same in every room and they are
 * what `/dev/tuning` and the test matrix are for.
 *
 * One of them describes *a room*: `energyGateDb`, the level below which a frame is
 * silence. It is the only parameter whose right value cannot be shipped, because it
 * depends on a fridge, a window onto a road, and where the iPad ended up on the music
 * desk. So that is the only one this module sets, from four seconds of the player not
 * playing.
 *
 * ## Why the C major chord is a check and not a measurement
 *
 * One chord is not a calibration. Fitting `harmonicWeights` or `seventhPenalty` to a
 * single sample would be numerology — the honest sample size for those is the 108-row
 * test matrix. What one chord *can* do is answer "does recognition basically work in this
 * room, at all", which is the question a new player actually has. So `assessCheckChord`
 * returns a verdict, never a patch: it either reassures, or it routes the player to the
 * tuning screen with an honest account of what was heard instead.
 */

import {
  DEFAULT_RECOGNIZER_PARAMS,
  RECOGNIZER_PARAM_DESCRIPTORS,
  mergeRecognizerParams,
  type ChordHypothesis,
  type DiagnosticsFrame,
  type PitchClass,
  type RecognizerEvent,
} from '@hc/shared';
import {
  DEFAULT_PROFILE_ID,
  listProfiles,
  saveProfile,
  setActiveProfileId,
  type TuningProfile,
} from '../tuning/profiles.js';

/**
 * How far above the measured noise floor the gate sits.
 *
 * Under it, the room itself opens the recogniser; far above it, quiet playing is silence.
 * Nine dB is a little over the level a fridge compressor or a passing car adds to a quiet
 * room, and comfortably below the ~25 dB a piano played *pianissimo* stands above one.
 */
export const NOISE_MARGIN_DB = 9;

/**
 * The gate never lands closer than this to something the recogniser demonstrably heard.
 * See `capGateBelowSignal`.
 */
export const SIGNAL_HEADROOM_DB = 6;

/**
 * A high percentile rather than the maximum.
 *
 * Four seconds of "stay quiet" contains a chair creak, a cough, or the player's hand
 * leaving the keys. The maximum is whichever of those happened; the 90th percentile is
 * the room. Deliberately not the mean either — the mean sits under the noise, and a gate
 * under the noise is a gate that fires on it.
 */
export const NOISE_FLOOR_PERCENTILE = 0.9;

/** C major. The one chord every player of any level can find without being taught it. */
export const CHECK_CHORD_PITCH_CLASSES: readonly PitchClass[] = Object.freeze([0, 4, 7]);

export const ROOM_PROFILE_ID = 'this-room';
export const ROOM_PROFILE_NAME = 'This room';

/**
 * What the check chord established.
 *
 * `'confirmed'` — a chord event arrived whose pitch-class set is C major. `'heard-other'`
 * — something was heard, but not that; `heard` is the reading, so the player can be told
 * what the app thought rather than just that it disagreed. `'nothing'` — no chord event at
 * all, which usually means the microphone, not the recogniser.
 */
export type CalibrationVerdict =
  { kind: 'confirmed' } | { kind: 'heard-other'; heard: ChordHypothesis } | { kind: 'nothing' };

/** The diagnostics frames out of a captured event stream, in arrival order. */
export function diagnosticsFrames(events: readonly RecognizerEvent[]): DiagnosticsFrame[] {
  const frames: DiagnosticsFrame[] = [];
  for (const event of events) {
    if (event.type === 'diagnostics') frames.push(event.frame);
  }
  return frames;
}

/**
 * The level of the room with nobody playing.
 *
 * With no frames at all nothing was measured, and the honest answer is not "the quietest
 * room imaginable" — it is the shipped default. The value returned in that case is the one
 * that makes `gateFromNoiseFloor` reproduce `DEFAULT_RECOGNIZER_PARAMS.energyGateDb`, so a
 * calibration that heard nothing changes nothing.
 */
export function measureNoiseFloor(frames: readonly DiagnosticsFrame[]): { noiseFloorDb: number } {
  const levels = frames
    .map((frame) => frame.energyDb)
    .filter((db) => Number.isFinite(db))
    .sort((a, b) => a - b);
  if (levels.length === 0) {
    return { noiseFloorDb: DEFAULT_RECOGNIZER_PARAMS.energyGateDb - NOISE_MARGIN_DB };
  }
  const index = Math.min(
    levels.length - 1,
    Math.max(0, Math.round(NOISE_FLOOR_PERCENTILE * (levels.length - 1))),
  );
  return { noiseFloorDb: levels[index] };
}

/**
 * Clamp to the range the tuning screen's slider can represent, and to whole decibels —
 * its step is 1 dB, and a gate of −60.4 would be a value the player could look at but
 * never reproduce by hand. `mergeRecognizerParams` owns the range, so the descriptor stays
 * the single source of truth for it.
 */
function clampGate(db: number): number {
  return mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, { energyGateDb: Math.round(db) })
    .energyGateDb;
}

/** The gate this room asks for: its noise floor plus a margin. */
export function gateFromNoiseFloor(noiseFloorDb: number): number {
  return clampGate(noiseFloorDb + NOISE_MARGIN_DB);
}

/**
 * Never gate above something we have just heard.
 *
 * A loud room — a fan, an open window onto traffic — produces a floor high enough that
 * floor + margin can land *above* the level of quiet playing. Applying that gate would
 * make the app go deaf immediately after telling the player it was set up, which is the
 * worst failure this flow could have: silent, and self-inflicted. So the gate is capped
 * below the loudest frame of the chord that was actually recognised. A non-finite
 * `loudestSignalDb` means no frames were seen, which is not evidence of anything.
 */
export function capGateBelowSignal(gateDb: number, loudestSignalDb: number): number {
  if (!Number.isFinite(loudestSignalDb)) return gateDb;
  return Math.min(gateDb, clampGate(loudestSignalDb - SIGNAL_HEADROOM_DB));
}

/** The loudest frame in a capture, or `-Infinity` when there are none. */
export function loudestEnergyDb(frames: readonly DiagnosticsFrame[]): number {
  let loudest = Number.NEGATIVE_INFINITY;
  for (const frame of frames) {
    if (Number.isFinite(frame.energyDb) && frame.energyDb > loudest) loudest = frame.energyDb;
  }
  return loudest;
}

/** Set equality, ignoring order and duplicates. The bass is deliberately not consulted. */
function isCheckChord(pitchClasses: readonly PitchClass[]): boolean {
  const heard = new Set(pitchClasses);
  return (
    heard.size === CHECK_CHORD_PITCH_CLASSES.length &&
    CHECK_CHORD_PITCH_CLASSES.every((pc) => heard.has(pc))
  );
}

/**
 * Did the room hear the chord the player was asked for?
 *
 * *Any* chord event matching C major confirms, not just the first: a rolled chord is
 * commonly read as its first two notes and then as the whole thing, and the player who
 * plays it twice because they were unsure should not be told they failed. When nothing
 * matched, the **last** reading is reported — after a roll settles, that is the one the
 * recogniser stands behind.
 *
 * Matching ignores the bass, exactly as branch matching does, so C major in first
 * inversion is C major (README rule 2).
 */
export function assessCheckChord(events: readonly RecognizerEvent[]): CalibrationVerdict {
  const heard: ChordHypothesis[] = [];
  for (const event of events) {
    if (event.type === 'chord') heard.push(event.detected.best);
  }
  if (heard.length === 0) return { kind: 'nothing' };
  if (heard.some((hypothesis) => isCheckChord(hypothesis.pitchClasses)))
    return { kind: 'confirmed' };
  return { kind: 'heard-other', heard: heard[heard.length - 1] };
}

/** A stored profile that is the shipped default, untouched — i.e. nobody has tuned yet. */
function isUntouchedDefault(profile: TuningProfile): boolean {
  if (profile.id !== DEFAULT_PROFILE_ID) return false;
  return RECOGNIZER_PARAM_DESCRIPTORS.every((descriptor) => {
    const stored = profile.params[descriptor.key];
    const shipped = DEFAULT_RECOGNIZER_PARAMS[descriptor.key];
    if (Array.isArray(stored) && Array.isArray(shipped)) {
      return stored.length === shipped.length && stored.every((value, i) => value === shipped[i]);
    }
    return stored === shipped;
  });
}

/**
 * Has this device ever been tuned?
 *
 * `listProfiles` always answers with at least the default profile — it is a floor, not a
 * row — so "any profile exists" is not the question. The question is whether any profile
 * exists that somebody *chose*, which is a saved profile of their own or a default they
 * have moved off its shipped values.
 *
 * Errors answer `true`. Storage that cannot be read is not evidence of a first run, and
 * the failure mode of guessing wrong is nagging a player who has already tuned.
 */
export async function hasAnyProfile(): Promise<boolean> {
  try {
    const profiles = await listProfiles();
    return profiles.some((profile) => !isUntouchedDefault(profile));
  } catch {
    return true;
  }
}

/**
 * Keep the measured gate as the active profile.
 *
 * Everything except the gate is the shipped default: this flow measured one number and
 * claims nothing about the other seventeen. `mergeRecognizerParams` clamps and copies the
 * harmonic-weight vector, so the frozen default array is never handed to storage.
 */
export async function saveRoomProfile(gateDb: number): Promise<TuningProfile> {
  const at = Date.now();
  const profile = await saveProfile({
    id: ROOM_PROFILE_ID,
    name: ROOM_PROFILE_NAME,
    params: mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, { energyGateDb: gateDb }),
    createdAt: at,
    updatedAt: at,
    notes: 'First-run calibration: energy gate measured from this room, everything else default.',
  });
  await setActiveProfileId(profile.id);
  return profile;
}
